import {
  WalletProviderPriority,
  getDefaultClient,
  getWalletProviderRegistry,
  hasExtension,
  registerExtension,
} from '@dynamic-labs-sdk/client/core';
import {
  BitKeepAdapter,
  OkxWalletAdapter,
  TokenPocketAdapter,
  TronLinkAdapter,
  TrustAdapter,
} from '@tronweb3/tronwallet-adapters';

import { registerTronNetworkProviderBuilder } from '../../registerTronNetworkProviderBuilder';
import type { TronWalletProvider } from '../../TronWalletProvider.types';
import { createWalletProviderForTronWalletAdapter } from '../utils/createWalletProviderForTronWalletAdapter';

export const TRON_WALLET_ADAPTERS_EXTENSION_KEY = 'tronWalletAdapters';

/**
 * Adds the Tron Wallet Adapters extension to the Dynamic client.
 *
 * This extension enables integration with wallets that have related Tron Wallet Adapters ('@tronweb3/tronwallet-adapters'),
 * automatically registering compatible wallets for use with the Dynamic SDK.
 *
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 */
export const addTronWalletAdaptersExtension = (
  client = getDefaultClient()
): void => {
  if (
    hasExtension({ extensionKey: TRON_WALLET_ADAPTERS_EXTENSION_KEY }, client)
  ) {
    return;
  }

  registerExtension(
    { extensionKey: TRON_WALLET_ADAPTERS_EXTENSION_KEY },
    client
  );

  registerTronNetworkProviderBuilder(client);

  const walletProviderRegistry = getWalletProviderRegistry(client);

  const tronWalletProviders: TronWalletProvider[] = [];

  // Create and register OKX wallet provider
  const okxWalletProvider = createWalletProviderForTronWalletAdapter({
    adapter: new OkxWalletAdapter(),
    dynamicClient: client,
  });
  if (okxWalletProvider) {
    tronWalletProviders.push(okxWalletProvider);
  }

  // Create and register TronLink wallet provider
  const tronLinkWalletProvider = createWalletProviderForTronWalletAdapter({
    adapter: new TronLinkAdapter(),
    dynamicClient: client,
  });
  if (tronLinkWalletProvider) {
    tronWalletProviders.push(tronLinkWalletProvider);
  }

  // Create and register Trust wallet provider
  const trustWalletProvider = createWalletProviderForTronWalletAdapter({
    adapter: new TrustAdapter(),
    dynamicClient: client,
  });
  if (trustWalletProvider) {
    tronWalletProviders.push(trustWalletProvider);
  }

  // Create and register TokenPocket wallet provider
  const tokenPocketWalletProvider = createWalletProviderForTronWalletAdapter({
    adapter: new TokenPocketAdapter(),
    dynamicClient: client,
  });
  if (tokenPocketWalletProvider) {
    tronWalletProviders.push(tokenPocketWalletProvider);
  }

  // Create and register Bitget wallet provider
  const bitgetWalletProvider = createWalletProviderForTronWalletAdapter({
    adapter: new BitKeepAdapter(),
    dynamicClient: client,
  });
  if (bitgetWalletProvider) {
    tronWalletProviders.push(bitgetWalletProvider);
  }

  tronWalletProviders.forEach((walletProvider) => {
    walletProviderRegistry.register({
      priority: WalletProviderPriority.WINDOW_INJECT,
      walletProvider,
    });
  });
};
